import { useCallback, useMemo } from 'react';
import useStore from '../context/useStore';
import BusinessHoursApi from 'opening_hours';
import { OrderMethod } from '../../types/constants';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { WorkScheduleUnit } from '../../types/availability';
import { OpenStreetMapHelpers } from '../../services/utils/OpenStreetMap/OpenStreetMapHelpers';
import { DayOfWeek, Hour, Minute } from '../../services/utils/DateHelpers';
import { StoreDetails } from '../../types/store';

export default function useBusinessHours(store?: StoreDetails) {
  const { storeData: _storeData } = useStore();
  const storeData = store ?? _storeData;

  const businessHours = useMemo(
    () =>
      new BusinessHoursApi(
        storeData.availability.work_schedule.length > 0
          ? OpenStreetMapHelpers.createScheduleString(storeData.availability.work_schedule)
          : 'Su-Sa off',
      ),
    [storeData.availability.work_schedule],
  );

  const deliveryHours = useMemo(() => {
    const schedule =
      storeData.availability.delivery_schedule ?? storeData.availability.work_schedule;

    return new BusinessHoursApi(
      schedule.length > 0 ? OpenStreetMapHelpers.createScheduleString(schedule) : 'Su-Sa off',
    );
  }, [storeData.availability.delivery_schedule, storeData.availability.work_schedule]);

  const getOrderMethodHours = useCallback(
    (method: OrderMethod) => (method !== OrderMethod.Delivery ? businessHours : deliveryHours),
    [businessHours, deliveryHours],
  );

  const isOrderMethodOpen = useCallback<
    (method: OrderMethod, time?: null | string | Moment) => boolean
  >(
    (method: OrderMethod, time?: null | string | Moment): boolean =>
      storeData.availability.open_now &&
      getOrderMethodHours(method).getState(moment(time ?? undefined).toDate()),
    [storeData.availability.open_now, getOrderMethodHours],
  );

  const findScheduleUnit = useCallback<
    (method: OrderMethod, time?: null | string | Moment) => null | WorkScheduleUnit
  >(
    (method: OrderMethod, time?: null | string | Moment): null | WorkScheduleUnit => {
      const hours = getOrderMethodHours(method);
      const date = moment(time ?? undefined).toDate();

      // Currently open
      if (hours.getState(date)) {
        return OpenStreetMapHelpers.toScheduleUnit(
          hours.prettifyValue({
            // @ts-expect-error rule index wrong ts format
            rule_index: hours.getMatchingRule(moment(time ?? undefined).toDate()),
          }),
        );
      }

      const from = moment(time ?? undefined).toDate();
      const to = moment(time ?? undefined)
        .add(1, 'week')
        .toDate();

      const interval = hours.getOpenIntervals(from, to)[0] ?? null;

      if (!interval) {
        return null;
      }

      const start = interval[0];
      const end = interval[1];

      return {
        open: {
          day: start.getDay() as DayOfWeek,
          hour: start.getHours() as Hour,
          minute: start.getMinutes() as Minute,
        },
        close: {
          day: end.getDay() as DayOfWeek,
          hour: end.getHours() as Hour,
          minute: end.getMinutes() as Minute,
        },
      };
    },
    [getOrderMethodHours],
  );

  return {
    businessHours,
    deliveryHours,
    getOrderMethodHours,
    isOrderMethodOpen,
    findScheduleUnit,
  };
}
